'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Sparkles, Loader2, X } from 'lucide-react';
import { CTAButton } from './CTAButton';
import { TYPE, RADII, SHADOW } from './tokens';
import { useExitIntent } from './useExitIntent';

/**
 * ExitIntentModal — last-chance email capture when the user's cursor
 * heads for the close button.
 *
 * UX design (F-500 2026):
 *   - One headline, one subhead, one input, one button. No scroll,
 *     no second CTA, no "see all features" link to distract.
 *   - Restates the trial promise (14 days · no card) — the user
 *     already saw it in the hero; the repeat is the value of the
 *     intercept.
 *   - Native `<dialog>` for built-in focus trap, esc-to-close,
 *     backdrop click. Same a11y baseline as SignupModal.
 *   - Success state replaces the form in place (no second modal,
 *     no toast jumping in from off-screen).
 *
 * Conversion plumbing:
 *   - Submit calls `POST /api/signup-leads` with source='exit_intent'.
 *     Reuses the abandoned-signup-recovery infrastructure shipped
 *     earlier today — the 3-step email sequence picks the lead up
 *     and nudges them back. Zero new server-side code needed.
 *   - PostHog events: `exit_intent_shown`, `exit_intent_email_captured`,
 *     `exit_intent_dismissed`. Lets us build a real funnel and A/B
 *     this against "no modal".
 */

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function emitPostHog(event: string, props?: Record<string, unknown>) {
  if (typeof window === 'undefined') return;
  const ph = (window as unknown as {
    posthog?: { capture?: (e: string, p?: unknown) => void };
  }).posthog;
  ph?.capture?.(event, props);
}

function getPostHogDistinctId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const ph = (window as unknown as { posthog?: { get_distinct_id?: () => string } })
      .posthog;
    return ph?.get_distinct_id?.() ?? null;
  } catch {
    return null;
  }
}

export function ExitIntentModal({ enabled = true }: { enabled?: boolean }) {
  const t = useTranslations('landing.exitIntent');
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const dismissReasonRef = useRef<'success' | 'manual' | null>(null);

  useExitIntent({
    enabled,
    onTrigger: () => {
      setOpen(true);
      emitPostHog('exit_intent_shown');
    },
  });

  // Sync React state → native <dialog>. Done here (not via attribute)
  // so we can call showModal()/close() which give us focus trap +
  // backdrop click for free.
  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    if (open && !dlg.open) {
      dlg.showModal();
    } else if (!open && dlg.open) {
      dlg.close();
    }
  }, [open]);

  // Native close event covers Esc + backdrop + .close() — single
  // place to attribute the dismissal source.
  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    const onClose = () => {
      setOpen(false);
      if (dismissReasonRef.current !== 'success') {
        emitPostHog('exit_intent_dismissed');
      }
      dismissReasonRef.current = null;
    };
    dlg.addEventListener('close', onClose);
    return () => dlg.removeEventListener('close', onClose);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrMsg(null);
    const normalized = email.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(normalized)) {
      setErrMsg(t('errors.invalidEmail'));
      return;
    }
    setSubmitting(true);

    const browserLocale =
      typeof document !== 'undefined' ? document.documentElement.lang || 'de' : 'de';

    try {
      const res = await fetch('/api/signup-leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        body: JSON.stringify({
          email: normalized,
          locale: browserLocale.slice(0, 2),
          source: 'exit_intent',
          posthog: getPostHogDistinctId(),
        }),
      });
      const data: { status?: string } | null = res.ok
        ? await res.json().catch(() => null)
        : null;

      const ok =
        data?.status === 'captured' ||
        data?.status === 'updated' ||
        data?.status === 'skipped_existing_user';

      if (!ok) {
        setErrMsg(t('errors.network'));
        setSubmitting(false);
        return;
      }

      emitPostHog('exit_intent_email_captured', { status: data?.status });
      setSuccess(true);
      setSubmitting(false);
    } catch {
      setErrMsg(t('errors.network'));
      setSubmitting(false);
    }
  };

  const dismiss = () => {
    dismissReasonRef.current = 'manual';
    setOpen(false);
  };

  const handleSuccessClose = () => {
    dismissReasonRef.current = 'success';
    setOpen(false);
  };

  return (
    <dialog
      ref={dialogRef}
      className={`fixed inset-0 m-auto w-[calc(100%-2rem)] max-w-md ${RADII.card} border border-gray-200/80 bg-white p-0 ${SHADOW.hero} backdrop:bg-gray-950/50 backdrop:backdrop-blur-sm motion-safe:open:animate-in motion-safe:open:fade-in motion-safe:open:zoom-in-95 motion-safe:open:duration-200`}
      aria-labelledby="exit-intent-title"
      aria-describedby="exit-intent-sub"
    >
      <div className="relative p-7 sm:p-8">
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('closeAria')}
          className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>

        {success ? (
          <SuccessPanel onClose={handleSuccessClose} />
        ) : (
          <>
            <div className="mb-5 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
              <Sparkles className="h-5 w-5" aria-hidden="true" />
            </div>

            <p className={`${TYPE.labelMd} text-blue-600`}>{t('eyebrow')}</p>
            <h2
              id="exit-intent-title"
              className={`mt-2 ${TYPE.headlineLg} text-gray-950`}
              style={{ textWrap: 'balance' as never }}
            >
              {t('title')}
            </h2>
            <p
              id="exit-intent-sub"
              className="mt-3 text-sm leading-relaxed text-gray-600 sm:text-[15px]"
            >
              {t('sub')}
            </p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-3" noValidate>
              <div>
                <label htmlFor="exit-intent-email" className="sr-only">
                  {t('emailLabel')}
                </label>
                <input
                  id="exit-intent-email"
                  type="email"
                  required
                  autoComplete="email"
                  inputMode="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errMsg) setErrMsg(null);
                  }}
                  placeholder={t('emailPlaceholder')}
                  aria-invalid={!!errMsg}
                  aria-describedby={errMsg ? 'exit-intent-error' : undefined}
                  className={`block h-12 w-full rounded-xl border bg-white px-4 text-base text-gray-950 placeholder:text-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-600/20 ${
                    errMsg
                      ? 'border-rose-400 focus:border-rose-500'
                      : 'border-gray-300 focus:border-blue-600'
                  }`}
                />
                {errMsg && (
                  <p
                    id="exit-intent-error"
                    className="mt-1.5 text-xs text-rose-600"
                    role="alert"
                  >
                    {errMsg}
                  </p>
                )}
              </div>

              <CTAButton
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    {t('submitting')}
                  </>
                ) : (
                  <>
                    {t('submit')}
                    <span aria-hidden="true">→</span>
                  </>
                )}
              </CTAButton>

              <p className="text-center text-[11px] leading-relaxed text-gray-500">
                {t('reassurance')}
              </p>
            </form>
          </>
        )}
      </div>
    </dialog>
  );
}

function SuccessPanel({ onClose }: { onClose: () => void }) {
  const t = useTranslations('landing.exitIntent.success');
  return (
    <div className="py-2 text-center">
      <div className="mx-auto mb-5 inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
        <svg
          viewBox="0 0 20 20"
          className="h-6 w-6"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M4 10l4 4 8-9" />
        </svg>
      </div>
      <h2 className={`${TYPE.headlineLg} text-gray-950`}>{t('title')}</h2>
      <p className="mx-auto mt-3 max-w-xs text-sm leading-relaxed text-gray-600">
        {t('body')}
      </p>
      <button
        type="button"
        onClick={onClose}
        className="mt-6 inline-flex h-10 items-center justify-center rounded-full px-5 text-sm font-semibold text-gray-700 transition hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
      >
        {t('close')}
      </button>
    </div>
  );
}
